import { NutritionFact } from './metadata';

export interface Menu {
  restaurantId: number;
  entrees?: readonly MenuEntree[] | null;
  sides?: MenuSidesAndDrinks[] | null;
  drinks?: MenuSidesAndDrinks[] | null;
  nonFoodItems?: MenuNonFoodItem[] | null;
}

export interface CompressedMenu {
  decompressedMenuSize: number;
  compressedMenu: string;
}

export enum PortionItemType {
  ExtraPortion = 'ExtraPortion',
  HalfPortion = 'HalfPortion',
}

export enum OptionItemType {
  Option = 'Option',
}

export enum GeneralItemType {
  Option = 'Option',
  Toppings = 'Toppings',
  Addon = 'Addon',
  Rice = 'Rice',
  Beans = 'Beans',
  Side = 'Side',
  Beverage = 'Beverage',
  Tortillas = 'Tortillas',
}

export type KnownMenuItemType = PortionItemType | OptionItemType | GeneralItemType;
export type MenuItemType = KnownMenuItemType | string;

export enum MenuItemCategory {
  Entree = 'Entree',
  Side = 'Side',
  Drink = 'Drink',
}

export enum TortillaItem {
  KidsQuesadilla = 'KidsQuesadilla',
  KidsByo = 'KidsBYO',
  Tortillas = 'Tortillas',
  Tacos = 'Tacos',
}

export enum KidsMealType {
  KidsBYO = `kid's-build-your-own`,
  KidsQuesadilla = `kid's-quesadilla`,
}

export interface MenuItem {
  itemCategory: MenuItemCategory | string;
  itemType: MenuItemType;
  itemId: string;
  itemName: string;
  posId: number;
  unitPrice: number;
  unitDeliveryPrice: number;
  unitCount: number;
  maxUnitCount: number;
  eligibleForDelivery: boolean;
  maxQuantity?: number;
  pricingReferenceItemId?: string | null;
  countTowardsCustomizationMax?: number;
  countTowardsContentMax?: number;
  defaultContent?: boolean;
  contentGroupName?: string;
  customizations?: ItemCustomization[] | null;
  isItemAvailable?: boolean;
  primaryFillingName?: string;
  entreeItemType?: string; // used by mono menu to track per entree item differences
  isUniversal?: boolean;
}

export interface GroupedMenuItems {
  // GroupName: MenuItems, if no GroupName, then item ID
  [key: string]: MenuItem[];
}

export interface VariantMenuItem extends MenuItem {
  maxContents: number;
  maxCustomizations: number;
  maxOnTheSideCustomizations: number;
  maxExtras: number;
  maxHalfs: number;
  maxExtrasPlusHalfs: number;
  contents?: MenuItem[] | null;
  primaryFillingName: string;
  contentGroups?: ContentGroup[];
}

export interface ContentGroup {
  contentGroupName: string;
  minQuantity: number;
  maxQuantity: number;
}

export interface MenuEntree extends VariantMenuItem {
  minTortilla: number;
  maxTortilla: number;
}

export enum ItemCustomizationId {
  Light = 1,
  Extra = 2,
  Side = 3,
  Half = 4,
}

export interface ItemCustomization {
  id: ItemCustomizationId;
  name: string;
  countTowardsCustomizationMax: number;
  countTowardsContentMax: number;
  countTowardsOnTheSideCustomizationMax: number;
}

export interface GroupedItemCustomization {
  id: string;
  name: string;
  countTowardsCustomizationMax?: number;
  countTowardsContentMax?: number;
  unitPrice: number;
  unitDeliveryPrice: number;
  posId: number;
  selectedText?: string;
}

export interface GroupedItemSelectionOption {
  id: string;
  name: string;
  price: number;
  deliveryPrice: number; // Undeliverable menu items will have this be set to price, but be unused
  posId: number;
  nutrition: NutritionFact[];
  showCustomizationPrice?: boolean;
  asCustomizationName?: string; // If the Menu Item is shown as a customization and a custom name is wanted for the selection
  showCustomizationNameWhenSelected?: boolean; // e.g. show "Extra" in UI instead of a check mark
  countTowardsContentMax?: number;
  countTowardsCustomizationMax?: number;
}

export interface MenuSidesAndDrinks {
  itemCategory: string;
  itemType: string;
  itemId: string;
  itemName: string;
  posId: number;
  unitPrice: number;
  unitDeliveryPrice: number;
  unitCount: number;
  maxUnitCount: number;
  maxQuantity: number;
  eligibleForDelivery: boolean;
  isItemAvailable?: boolean;
  isUniversal?: boolean;
}

export interface MenuItemLookupResult {
  menuItemName: string;
  menuItemType: MenuItemType;
  primaryFillingName?: string;
  unitPrice: number;
  unitDeliveryPrice: number;
  isEligibleForDelivery: boolean;
  isItemAvailable?: boolean;
}

export type TopLevelMenuType = MenuEntree | MenuSidesAndDrinks;

export interface UndeliverableItem {
  id: string;
  name: string;
}

export interface MenuNonFoodItem {
  itemCategory: string;
  itemType: string;
  itemId: string;
  itemName: string;
  posId: number;
  unitPrice: number;
  unitDeliveryPrice: number;
  unitCount: number;
  maxQuantity: number;
  eligibleForDelivery: boolean;
  isUniversal: boolean;
  isItemAvailable?: boolean;
}

export interface PricingInformation {
  formattedPrice: string;
  price: number;
}
